import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import C3 from "c3";
import {
  ICreateModelPanelProps,
  ILabelSelectItem,
  createLabelSelectItems,
  changeLabelSelectItemValue,
  createChartTable,
  IOptionItems,
} from "../model/CreateModelPanel";
import ProfileListPanel from "./ProfileListPanel";
import ChannelSettingDialog from "../common/ChannelSettingDialog";
import {
  getFileChannels,
  FileChannelItem,
} from "../../../../../webapi/service/getFileChannels";
import { createProfile } from "../../../../../webapi/service/createProfile";
import {
  drawCharts,
  getChartTableInfo,
  ChartSize,
  createChartTableStyle,
  getSortProts,
} from "../common/chart";
import {
  ProjectStatus,
  ProfileSelect,
  ChartList,
} from "../../../../../common/constants";
import { WebAPI } from "../../../../../webapi/constants";
import { IProtRow } from "../../../../../webapi/service/getFIles";

function isCreateEnabled(selectItems: ILabelSelectItem[]): boolean {
  const use = selectItems.find((item) => {
    return item.value === ProfileSelect.USE;
  });
  return use ? true : false;
}

export interface IProps extends ICreateModelPanelProps, WithTranslation {}

interface IState {
  /**
   * チャート一覧
   */
  selectItems: ILabelSelectItem[];
  /**
   * チャネル情報一覧
   */
  channels: FileChannelItem[];
  /**
   * プロファイル作成対象ファイルID一覧
   */
  fileids: string[];
  /**
   * チャネル設定ダイアログを呼び出す場合はtrue
   */
  showChannelSetting: boolean;
  /**
   * プロファイルが作成可能な場合はtrue
   */
  create: boolean;
}

class CreateProfilePanel extends React.Component<IProps, IState> {
  private readonly chartid_fmt = "chart_create_prof_";
  private readonly DefaultChartSize: ChartSize = {
    width: ChartList.PROFILE_WIDTH,
    height: ChartList.PROFILE_HEIGHT,
  };
  private readonly MaxDataNum = ChartList.MAX_DATA_NUM;
  private readonly StatusDone = ProjectStatus.DONE;
  private _charts: C3.ChartAPI[] | null = null;
  private _chartSize: ChartSize = this.DefaultChartSize;

  constructor(props: IProps) {
    super(props);

    this.state = {
      selectItems: [],
      channels: [],
      fileids: [],
      showChannelSetting: false,
      create: false,
    };

    this.handleCreate = this.handleCreate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleChangeBulk = this.handleChangeBulk.bind(this);
    this.handleOpenChannelSetting = this.handleOpenChannelSetting.bind(this);
    this.handleChannelSettingOK = this.handleChannelSettingOK.bind(this);
    this.handleChannelSettingCancel =
      this.handleChannelSettingCancel.bind(this);
    this.chartSize = this.chartSize.bind(this);
  }

  chartSize(size?: ChartSize) {
    if (size) {
      this._chartSize = size;
    }
    return this._chartSize;
  }

  createCharts(prots: IProtRow[]): void {
    const { mainSpaceWidth } = this.props;
    const { chartWidth, chartHeight } = getChartTableInfo(
      mainSpaceWidth,
      this.DefaultChartSize,
      true
    );
    this._charts = drawCharts(prots, this.chartid_fmt, chartWidth, chartHeight);
    this.chartSize({ width: chartWidth, height: chartHeight });
  }

  destroyCharts(): void {
    if (this._charts) {
      this._charts.forEach((chart) => {
        chart.destroy();
      });
      this._charts = null;
    }
  }

  getPros() {
    const { prots, sortIds } = this.props;
    return getSortProts(prots, sortIds).filter((prot) => {
      return prot.datanum <= this.MaxDataNum;
    });
  }

  componentDidMount() {
    const { onLoading } = this.props;
    setTimeout(() => {
      const prots = this.getPros();
      this.createCharts(prots);
      this.setState({ selectItems: createLabelSelectItems(prots) });
      onLoading(false);
    });
    onLoading(true);
  }

  componentWillUnmount() {
    this.destroyCharts();
  }

  handleCreate() {
    const { projectInfo, showConfirm, t } = this.props;
    if (projectInfo.pcreatests === this.StatusDone) {
      showConfirm({
        title: t("profile_create_overwrite"),
        message: t("profile_create_overwrite_message"),
        onOk: () => {
          this.handleOpenChannelSetting();
        },
      });
    } else {
      this.handleOpenChannelSetting();
    }
  }

  handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const id = e.target.id;
    const value: number = parseInt(e.target.value);
    const selectItems = changeLabelSelectItemValue(
      this.state.selectItems.slice(),
      id,
      value
    );
    this.setState({
      selectItems: selectItems,
      create: isCreateEnabled(selectItems),
    });
  }

  handleChangeBulk(mode: number) {
    const selectItems = this.state.selectItems.slice();
    selectItems.forEach((item) => {
      item.value = mode;
    });
    this.setState({
      selectItems: selectItems,
      create: isCreateEnabled(selectItems),
    });
  }

  handleOpenChannelSetting() {
    const { authService, projectInfo, onLoading, onCommunicationError } =
      this.props;
    const { selectItems } = this.state;

    onLoading(true);

    const fileids: string[] = [];
    selectItems.forEach((item) => {
      if (item.value === 1) {
        fileids.push(item.fileid);
      }
    });

    getFileChannels({
      authService: authService,
      projectid: projectInfo.projectid,
      fileids,
      success: (channels) => {
        if (channels.length === 0) {
          onCommunicationError(WebAPI.ID_NO_COMMON_CHANNEL, undefined);
        } else {
          this.setState({
            showChannelSetting: true,
            channels: channels,
            fileids: fileids,
          });
          onLoading(false);
        }
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleChannelSettingOK(channels: string[]) {
    const {
      authService,
      projectInfo,
      onLoading,
      onTop,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    const { fileids } = this.state;
    onLoading(true);
    this.handleChannelSettingCancel();
    resetUnoperatedTimeout();
    createProfile({
      authService: authService,
      projectid: projectInfo.projectid,
      fileid: fileids,
      channels: channels,
      success: () => {
        onTop();
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleChannelSettingCancel() {
    this.setState({ showChannelSetting: false });
  }

  render() {
    const { mainSpaceWidth, onBack, t } = this.props;
    const { selectItems, create, showChannelSetting, channels } = this.state;
    const chartTableStyle = createChartTableStyle(
      mainSpaceWidth,
      this.DefaultChartSize,
      this._charts,
      this.chartSize
    );
    const optionItems: IOptionItems[] = [
      { text: t("profile_settings_use"), value: ProfileSelect.USE },
      {
        text: t("profile_settings_unused"),
        value: ProfileSelect.UNUSED,
      },
    ];
    const contents = createChartTable(
      this.getPros(),
      selectItems,
      this.chartid_fmt,
      optionItems,
      this.handleChange
    );
    return (
      <React.Fragment>
        <ProfileListPanel
          label={t("profile_create")}
          chartTableStyle={chartTableStyle}
          chartTable={contents}
          isCreateEnabled={create}
          onBack={onBack}
          onCreate={this.handleCreate}
          onChangeAll={this.handleChangeBulk}
        />
        <ChannelSettingDialog
          title={t("profile_settings_channel")}
          caption={t("profile_settings_channel_caption")}
          show={showChannelSetting}
          channels={channels}
          onOk={this.handleChannelSettingOK}
          onCancel={this.handleChannelSettingCancel}
        />
      </React.Fragment>
    );
  }
}

/**
 * プロファイル作成画面
 */
export default withTranslation()(CreateProfilePanel);
